import { useEffect, useState } from 'react';
import TaskColumn from '../../components/tasks/kanbanBoard/taskColumn';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  closestCorners,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import ModalTask from '../../components/tasks/modalTask/modalTask';
import { fetchTasks } from 'common/src/api/tasks';
import SortableTask from '../../components/tasks/kanbanBoard/sortableTask';
import useUserStore from '../../store/useUserStore';
import { Tasks } from 'common/src/utils/types';
import { Button, Select, SelectItem } from '@nextui-org/react';
import { FaPlus } from 'react-icons/fa';
import ModalCreateTask from '../../components/tasks/modalTask/modalCreateTask/ModalCreateTask';
import ModalDeleteTask from '../../components/tasks/modalTask/modalDeleteTask';
import useGeneralStore from '../../store/useGeneralStore';
import { getInitials } from 'common/src/utils/functions';
import { ModalAddComment } from '../../components/tasks/modalTask/modalAddComment';
import ModalNotifyAsker from '../../components/tasks/modalTask/modalNotifyAsker';
// import { sendStatusChangeEmail } from 'common/src/api/emails/askerEmails';
// import { capturePayment } from 'common/src/api/mercado_pago';
import { toast } from 'react-toastify';
import TaskStatusService from 'common/src/api/services/task_status_service';
import { ROLES, TASK_STATUS } from '../../types/constans';

const TasksPage = () => {
  const { userProfile } = useUserStore();
  const { users, fetchUsers } = useGeneralStore();
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [tasks, setTasks] = useState<Tasks[]>([]);
  const [selectedTask, setSelectedTask] = useState<Tasks | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Tasks | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingTask, setDeletingTask] = useState<Tasks | null>(null);
  const [isAddCommentModalOpen, setIsAddCommentModalOpen] = useState(false);
  const [selectedTaskForComment, setSelectedTaskForComment] = useState<Tasks | null>(null);
  const [, setIsUpdating] = useState(false);
  const [isNotifyModalOpen, setIsNotifyModalOpen] = useState(false);
  const [taskToNotify, setTaskToNotify] = useState<Tasks | null>(null);

  const handleAddComment = (task: Tasks) => {
    setSelectedTaskForComment(task);
    setIsAddCommentModalOpen(true);
  };

  const handleDeleteClick = (task: Tasks) => {
    setDeletingTask(task);
    setIsDeleteModalOpen(true);
  };

  const loadTasks = async () => {
    try {
      let assignedUserId: number | undefined;
      if (userProfile?.role.includes(ROLES.ADMIN) && selectedUser) {
        assignedUserId = parseInt(selectedUser);
      } else if (!userProfile?.role.includes(ROLES.ADMIN)) {
        assignedUserId = userProfile?.id;
      }

      const { data } = await fetchTasks(
        undefined,
        undefined,
        'asc',
        '2024-07-29T04:00:00',
        assignedUserId
      );
      setTasks(data);
      console.log('data', data);
      setFetchError(null);
    } catch (error) {
      if (error instanceof Error) {
        setFetchError(error.message);
      } else {
        setFetchError('An unknown error occurred');
      }
      setTasks([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    loadTasks();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDragStart = (event: any) => {
    console.log('event', event.active.id);
    setActiveId(event.active.id.split('-')[1]);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDragOver = async (event: any) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) return;

    const activeStatus = activeId.split('-')[0];
    const overStatus = overId.split('-')[0];

    if (activeStatus !== overStatus) {
      setTasks((prev) => {
        const activeTask = prev.find((t) => t.id === Number(activeId.split('-')[1]));
        if (!activeTask) return prev;

        const updatedTasks = prev.map((task) => {
          if (task.id === activeTask.id) {
            return { ...task, status: overStatus };
          }
          return task;
        });

        return updatedTasks;
      });

      // No necesitamos actualizar la base de datos aquí, lo haremos en handleDragEnd
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setIsUpdating(true);
    try {
      const { active, over } = event;
      setActiveId(null);
      if (!over) return;

      const activeId =
        typeof active.id === 'string' ? active.id.split('-')[1] : active.id.toString();
      const overId = over.id;

      if (typeof overId !== 'string') return;

      const activeTask = tasks?.find((task) => task.id === +activeId);
      if (!activeTask) {
        console.error('Active task not found');
        return;
      }

      let newStatus;
      if (overId.includes(TASK_STATUS.PENDING)) newStatus = 'pending';
      if (overId.includes(TASK_STATUS.ACCEPTED)) newStatus = 'accepted';
      if (overId.includes(TASK_STATUS.IN_APPLICATION)) newStatus = 'in_application';
      if (overId.includes(TASK_STATUS.ASSIGNED)) newStatus = 'assigned';
      if (overId.includes(TASK_STATUS.PROGRESS)) newStatus = 'progress';
      if (overId.includes(TASK_STATUS.COMPLETE)) newStatus = 'complete';
      if (overId.includes(TASK_STATUS.REJECTED)) newStatus = 'rejected';

      // Se actualiza el estado de la tarea en la base de datos
      const updatedTask = await TaskStatusService.changeTaskStatus(activeId, newStatus);

      if (updatedTask) {
        setTaskToNotify(updatedTask);
        setIsNotifyModalOpen(true);

        setTasks((prevTasks) =>
          prevTasks.map((task) => (task.id === +activeId ? updatedTask : task))
        );
      }
    } catch (error) {
      console.error('Error in handleDragEnd:', error);
      toast.error('Error al actualizar el estado de la tarea');
    } finally {
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    console.log('isNotifyModalOpen changed:', isNotifyModalOpen);
    console.log('taskToNotify:', taskToNotify);
  }, [isNotifyModalOpen, taskToNotify]);

  const getTasksByStatus = (status: string) => {
    return (
      tasks?.filter((task) => {
        let statusMatch = task.status === status;
        // Special case for progress column
        if (status === TASK_STATUS.PROGRESS) {
          statusMatch = task.status === TASK_STATUS.PROGRESS || task.status === 'in_progress';
        }
        const userMatch = userProfile?.role.includes(ROLES.ADMIN)
          ? !selectedUser || task.assigned_user_id === parseInt(selectedUser)
          : task.assigned_user_id === userProfile?.id;
        return statusMatch && userMatch;
      }) || []
    );
  };

  const handleUserSelect = (value: string) => {
    setSelectedUser(value === 'all' ? null : value);
  };

  const handleTaskClick = (task: Tasks) => {
    console.log('Task selected:', task);
    setSelectedTask(task);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTask(null);
  };

  const handleEditClick = (task: Tasks) => {
    setEditingTask(task);
    setIsEditModalOpen(true);
  };

  const activeTask = tasks?.find((task) => task.id === Number(activeId));

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="items-center pb-4 space-y-4">
          <div className="flex items-center space-x-2">
            <h2 className="text-2xl font-semibold">Gestion de Tasks</h2>
            <Button
              color="primary"
              size="sm"
              className="bg-purple-400 rounded-full"
              isIconOnly
              onPress={() => setIsCreateModalOpen(true)}
            >
              <FaPlus size={18} />
            </Button>
          </div>
          {userProfile?.role.includes('admin') && (
            <Select
              items={[
                { label: 'Todos los usuarios', value: '' },
                ...users.map((user) => ({
                  label: `${user.first_name} ${user.last_name}`,
                  value: user.id.toString(),
                })),
              ]}
              label="Filtrar por usuario"
              placeholder="Seleccionar un usuario"
              className="max-w-xs"
              variant="bordered"
              onChange={(e) => handleUserSelect(e.target.value)}
              defaultSelectedKeys={['']}
            >
              {(user) => (
                <SelectItem key={user.value} value={user.value}>
                  {user.label}
                </SelectItem>
              )}
            </Select>
          )}
        </div>

        <div className="flex-grow overflow-hidden ">
          {fetchError && <p>{fetchError}</p>}
          {tasks && (
            <div className="flex space-x-4 h-full overflow-x-auto">
              {[
                'pending',
                'accepted',
                'in_application',
                'assigned',
                'progress', // This will now include both 'progress' and 'in_progress'
                'complete',
                'rejected',
              ].map((status) => (
                <SortableContext
                  key={status}
                  items={getTasksByStatus(status).map((task) => `${task.status}-${task.id}`)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="flex-shrink-0 w-[30vw]" id={`column-${status}`}>
                    <TaskColumn
                      key={status}
                      title={
                        status === TASK_STATUS.PROGRESS
                          ? 'En Progreso'
                          : status.charAt(0).toUpperCase() + status.slice(1)
                      }
                      tasks={getTasksByStatus(status)}
                      onTaskClick={handleTaskClick}
                      onEdit={handleEditClick}
                      onDelete={handleDeleteClick}
                      onAddComment={handleAddComment}
                    />
                  </div>
                </SortableContext>
              ))}
            </div>
          )}
        </div>
        <DragOverlay>
          {activeTask ? (
            <SortableTask
              id={`${activeTask.status}-${activeTask.id}`}
              cardTitle={activeTask.category}
              cardCategory={activeTask.description}
              onClick={() => {
                console.log('clicked');
              }}
              assignedUserInitials={getInitials(
                activeTask?.User?.first_name || '',
                activeTask?.User?.last_name || ''
              )}
              assignedUserName={
                activeTask?.User?.first_name && activeTask?.User?.last_name
                  ? `${activeTask?.User?.first_name} ${activeTask?.User?.last_name}`
                  : 'No Asignadooww'
              }
              price={activeTask?.price || 0}
              name={activeTask?.name || ''}
              last_name={activeTask?.last_name || ''}
              date=""
              onEdit={() => activeTask && handleEditClick(activeTask)}
              onDelete={() => activeTask && handleDeleteClick(activeTask)}
              phone_number={activeTask?.phone_number || ''}
              onAddComment={() => activeTask && handleAddComment(activeTask)}
              task={activeTask}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
      <ModalTask
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        fetchTasks={() => {
          loadTasks();
          return Promise.resolve();
        }}
        task={selectedTask || ({} as Tasks)}
        onReset={() => {
          console.log('reset');
        }}
      />
      {isEditModalOpen && (
        <ModalCreateTask
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          task={editingTask || ({} as Tasks)}
          fetchTasks={() => {
            loadTasks();
            return Promise.resolve();
          }}
          isEditMode={true}
        />
      )}
      {isCreateModalOpen && (
        <ModalCreateTask
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          fetchTasks={() => {
            loadTasks();
            return Promise.resolve();
          }}
          isEditMode={false}
        />
      )}
      {isDeleteModalOpen && deletingTask && (
        <ModalDeleteTask
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          task={deletingTask}
          fetchTasks={() => {
            loadTasks();
            return Promise.resolve();
          }}
        />
      )}
      {isAddCommentModalOpen && selectedTaskForComment && (
        <ModalAddComment
          isOpen={isAddCommentModalOpen}
          onClose={() => setIsAddCommentModalOpen(false)}
          taskId={selectedTaskForComment?.id.toString()}
          userId={userProfile?.id.toString()}
          onCommentAdded={() => {
            loadTasks();
            setIsAddCommentModalOpen(false);
          }}
        />
      )}
      <ModalNotifyAsker
        isOpen={isNotifyModalOpen}
        onClose={() => setIsNotifyModalOpen(false)}
        task={taskToNotify}
      />
    </>
  );
};

export default TasksPage;
